import { Money } from "@redotech/money";
import { memo } from "react";
import { Flex } from "./flex";
import { MoneyText } from "./fx/money-text";
import { ProductListItem } from "./product-list-item";
import { ProductListItemQuantity } from "./product-list-item-quantity";
import * as styles from "./product-list-row.module.css";
import { Text } from "./text";

const BASIS_WIDTH = "80px";
export const ProductListRow = memo(function ProductListRow({
  title,
  imageUrl,
  quantity,
  price,
  priceMoney,
  variantTitle,
  sku,
  serialNumbers,
  customAttributes,
  bin,
  showOnHand = false,
  onHand,
  showAvailable = false,
  available,
  amountPaidMoney,
  emphasizeQuantity = true,
  disableTooltip = false,
  imageTooltipPlacement = "left",
}: {
  title: string;
  imageUrl: string | null | undefined;
  quantity: number;
  price: string;
  priceMoney?: Money | null;
  variantTitle: string | null | undefined;
  sku: string | null | undefined;
  serialNumbers?: readonly string[] | null;
  customAttributes?: { key: string; value: string }[];
  bin?: string | null;
  showOnHand?: boolean;
  onHand?: number | null;
  showAvailable?: boolean;
  available?: number | null;
  amountPaidMoney?: Money | null;
  emphasizeQuantity?: boolean;
  disableTooltip?: boolean;
  imageTooltipPlacement?: "left" | "right" | "top" | "bottom";
}) {
  return (
    <Flex flexDirection="column" p="xl" w="full">
      <Flex align="center" gap="lg" justify="space-between" w="full">
        <Flex grow={1} minW="none">
          <ProductListItem
            disableTooltip={disableTooltip}
            imageTooltipPlacement={imageTooltipPlacement}
            imageUrl={imageUrl}
            serialNumbers={serialNumbers}
            sku={sku}
            title={title}
            variantTitle={variantTitle}
          />
        </Flex>
        <Flex align="center" gap="xl" shrink={0}>
          {bin && (
            <Text fontSize="xs" textColor="secondary">
              {bin}
            </Text>
          )}
          {showOnHand && (
            <Flex basis={BASIS_WIDTH} justify="center" shrink={0}>
              <Text
                className={styles.inventoryColumn}
                fontSize="md"
                fontWeight="regular"
                textAlign="center"
                textColor="secondary"
              >
                {onHand ?? "—"}
              </Text>
            </Flex>
          )}
          {showAvailable && (
            <Flex basis={BASIS_WIDTH} justify="center" shrink={0}>
              <Text
                className={styles.inventoryColumn}
                fontSize="md"
                fontWeight="regular"
                textAlign="center"
                textColor="secondary"
              >
                {available ?? "—"}
              </Text>
            </Flex>
          )}
          <Flex align="center" basis={BASIS_WIDTH} justify="center" shrink={0}>
            <ProductListItemQuantity
              emphasizeQuantity={emphasizeQuantity}
              price={price}
              priceMoney={priceMoney}
              quantity={quantity}
            />
          </Flex>
          {amountPaidMoney !== undefined && (
            <Text
              className={styles.amountPaidColumn}
              fontSize="sm"
              fontWeight="medium"
              textAlign="right"
            >
              <MoneyText value={amountPaidMoney} />
            </Text>
          )}
        </Flex>
      </Flex>
      {customAttributes && customAttributes.length > 0 && (
        <ProductListItemAttributes customAttributes={customAttributes} />
      )}
    </Flex>
  );
});

export const ProductListItemAttributes = memo(
  function ProductListItemAttributes({
    customAttributes,
  }: {
    customAttributes: { key: string; value: string }[];
  }) {
    return (
      <Flex flexDirection="column" gap="sm">
        <Text fontSize="xxs">Custom attributes</Text>
        {customAttributes.map((attribute) => (
          <Text fontSize="xxs" key={attribute.key} textColor="secondary">
            {attribute.key}: {attribute.value}
          </Text>
        ))}
      </Flex>
    );
  },
);

export const ItemTableHeader = memo(function ItemTableHeader({
  flatTopCorners = true,
  showBin = false,
  showOnHand = false,
  showAvailable = false,
  showAmountPaid = false,
}: {
  flatTopCorners?: boolean;
  showBin?: boolean;
  showOnHand?: boolean;
  showAvailable?: boolean;
  showAmountPaid?: boolean;
}) {
  return (
    <Flex
      bgColor="secondary"
      borderBottomWidth="1px"
      borderColor="primary"
      borderRadius={flatTopCorners ? "none" : "md"}
      borderStyle="solid"
      className={styles.flatBottomCorners}
      justify="space-between"
      px="xl"
      py="lg"
      w="full"
    >
      <Text fontSize="xxs" fontWeight="medium">
        Item name
      </Text>

      <Flex gap="xl" shrink={0}>
        {showBin && (
          <Text fontSize="xxs" fontWeight="medium">
            Bin
          </Text>
        )}
        {showOnHand && (
          <Flex basis={BASIS_WIDTH} justify="center" shrink={0}>
            <Text fontSize="xxs" fontWeight="medium">
              On hand
            </Text>
          </Flex>
        )}
        {showAvailable && (
          <Flex basis={BASIS_WIDTH} justify="center" shrink={0}>
            <Text fontSize="xxs" fontWeight="medium">
              Available
            </Text>
          </Flex>
        )}
        <Flex basis={BASIS_WIDTH} justify="center" shrink={0}>
          <Text fontSize="xxs" fontWeight="medium">
            Quantity
          </Text>
        </Flex>
        {showAmountPaid && (
          <Text
            className={styles.amountPaidColumn}
            fontSize="xxs"
            fontWeight="medium"
            textAlign="right"
          >
            Amount paid
          </Text>
        )}
      </Flex>
    </Flex>
  );
});
